/**
 * Splitter utility: make a two-column container resizable with a draggable divider.
 * Usage: installSplit('.portfolio-split-layout', 'portfolio-split')
 */

export function installSplit(selector, storageKey) {
  try {
    const container = document.querySelector(selector);
    if (!container) return;
    // Ensure exactly two element children
    const children = Array.from(container.children).filter((n) => n.nodeType === 1);
    if (children.length !== 2) return;

    // Idempotent: if divider exists, do nothing
    if (container.__splitInstalled) return;

    // Prepare container as grid (3 columns: left | divider | right)
    container.style.display = 'grid';
    container.style.alignItems = 'stretch';
    // Mark for CSS (used by panel system to enforce layout-safe rules)
    try { container.setAttribute('data-split-container', ''); } catch(_) {}
    // Respect the theme's original gap; do not override container gaps here
    try { container.style.columnGap = ''; container.style.gap = ''; } catch(_) {}

    const left = children[0];
    const right = children[1];

    // Restore widths from storage if available
    const saved = safeRead(storageKey);
    const w = container.clientWidth || 1000;
    // Read CSS variable --split-handle-width when available
    let handleW = 6;
    try {
      const cssVal = getComputedStyle(container).getPropertyValue('--split-handle-width');
      const num = parseInt(String(cssVal).trim(), 10);
      if (Number.isFinite(num) && num > 0) handleW = num;
    } catch (_) {}
    // Use percentage-based split so both sides resize symmetrically
    const initialRatio = (saved && typeof saved.ratio === 'number')
      ? Math.max(0.15, Math.min(0.85, saved.ratio))
      : 0.75; // default bias: canvas (left) wider
    const leftPct = Math.round(initialRatio * 1000) / 10; // 1 decimal
    const rightPct = 100 - leftPct;
    // Keep total width == 100% by subtracting half of handle from each side
    // Use minmax(0, ...) so tracks can shrink below min-content of inner nodes
    container.style.gridTemplateColumns = `minmax(0, calc(${leftPct}% - ${handleW/2}px)) ${handleW}px minmax(0, calc(${rightPct}% - ${handleW/2}px))`;
    // Ensure children don't impose their own widths
    left.style.width = 'auto'; right.style.width = 'auto';

    // Create divider
    const divider = document.createElement('div');
    divider.className = 'split-divider';
    divider.setAttribute('role', 'separator');
    divider.setAttribute('aria-orientation', 'vertical');
    divider.style.cursor = 'col-resize';
    divider.style.width = `${handleW}px`;
    divider.style.flex = '0 0 auto';
    divider.style.background = 'transparent';
    divider.style.position = 'relative';
    divider.style.height = '100%';
    // Grid placement: [left, divider, right]
    left.style.gridColumn = '1';
    divider.style.gridColumn = '2';
    right.style.gridColumn = '3';

    // Insert divider
    container.insertBefore(divider, right);

    // Drag logic
    let dragging = false;
    let startX = 0;
    let startLeft = 0;
    let currentRatio = initialRatio;

    const onPointerMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      const min = 160; // min width for each panel (tighter to allow wider opposite panel)
      const max = container.clientWidth - min; // ensure right min width
      let next = Math.max(min, Math.min(max, startLeft + dx));
      currentRatio = Math.max(0.15, Math.min(0.85, next / container.clientWidth));
      const lp = Math.round(currentRatio * 1000) / 10; // 1 decimal
      const rp = 100 - lp;
      container.style.gridTemplateColumns = `minmax(0, calc(${lp}% - ${handleW/2}px)) ${handleW}px minmax(0, calc(${rp}% - ${handleW/2}px))`;
    };

    const onPointerUp = () => {
      if (!dragging) return;
      dragging = false;
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      // Persist as ratio (left fraction 0..1)
      try {
        localStorage.setItem(`split:${storageKey}`, JSON.stringify({ ratio: currentRatio }));
      } catch (_) {}
    };

    divider.addEventListener('pointerdown', (e) => {
      dragging = true;
      startX = e.clientX;
      startLeft = left.getBoundingClientRect().width;
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    });

    // Double-click to reset 50/50
    divider.addEventListener('dblclick', () => {
      currentRatio = 0.5;
      container.style.gridTemplateColumns = `minmax(0, calc(50% - ${handleW/2}px)) ${handleW}px minmax(0, calc(50% - ${handleW/2}px))`;
      try { localStorage.setItem(`split:${storageKey}`, JSON.stringify({ ratio: 0.5 })); } catch (_) {}
    });

    // Keep ratio on window resize
    const onResize = () => {
      const lp = Math.round(currentRatio * 1000) / 10;
      const rp = 100 - lp;
      container.style.gridTemplateColumns = `minmax(0, calc(${lp}% - ${handleW/2}px)) ${handleW}px minmax(0, calc(${rp}% - ${handleW/2}px))`;
    };
    window.addEventListener('resize', onResize);
    // Teardown hook (best-effort)
    container.__splitCleanup = () => window.removeEventListener('resize', onResize);

    container.__splitInstalled = true;
  } catch (e) {
    // swallow errors — non-critical UI enhancement
  }
}

function safeRead(key) {
  try {
    const raw = localStorage.getItem(`split:${key}`);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export default { installSplit };
